import React, { useState } from 'react';
import { Input, Button, Modal, Spin } from 'antd';
import { usePensionForm } from './hooks/usePensionForm';

// Reusable NumInput to avoid InputNumber focus issues
const NumInput = ({ value, onChange, disabled, width = 150 }: {
  value: number; onChange: (v: number) => void; disabled: boolean; width?: number;
}) => {
  const [text, setText] = useState(String(value));
  React.useEffect(() => { setText(String(value)); }, [value]);
  return (
    <Input value={text} onChange={e => setText(e.target.value)}
      onBlur={() => { const n = parseFloat(text); onChange(isNaN(n) ? 0 : n); }}
      disabled={disabled} style={{ width, textAlign: 'right' }} />
  );
};

const P9138Form: React.FC = () => {
  const f = usePensionForm();
  const [searchText, setSearchText] = useState('');

  const handleDelete = () => {
    if (!f.current) return;
    Modal.confirm({
      title: '確認刪除', content: `確定要刪除提繳工資 ${f.current.f938_01}？`,
      okText: '刪除', okType: 'danger', cancelText: '取消', onOk: f.doDelete,
    });
  };
  const modeLabel = f.mode === 'add' ? '新增' : f.mode === 'edit' ? '修改' : '瀏覽';

  return (
    <Spin spinning={f.loading}>
      <div className="max-w-2xl mx-auto p-6">
        <h2 className="text-xl font-bold mb-4">
          退休金參數建檔 (pw9138f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {f.records.length > 0 ? `${f.currentIdx + 1} / ${f.records.length}` : '無資料'}
          </span>
        </h2>
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
          <div><b>BR-V01</b>: 月提繳比率不得大於 6%</div>
          <div><b>BR-C01</b>: 自動計算日額/月額（提繳工資 x 比率 / 100 / 30）</div>
        </div>
        <div className="flex items-center gap-2 mb-4">
          <Input placeholder="提繳工資查詢" value={searchText} onChange={e => setSearchText(e.target.value)}
            onPressEnter={() => searchText.trim() && f.quickSearch(searchText.trim())}
            disabled={f.isEditing} style={{ width: 150 }} />
          <Button onClick={() => searchText.trim() && f.quickSearch(searchText.trim())} disabled={f.isEditing}>查詢</Button>
        </div>
        <div className="flex items-center gap-1 mb-4">
          <Button size="small" onClick={f.navFirst} disabled={f.isEditing || !f.records.length}>|&lt;</Button>
          <Button size="small" onClick={f.navPrev} disabled={f.isEditing || f.currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={f.navNext} disabled={f.isEditing || f.currentIdx >= f.records.length - 1}>&gt;</Button>
          <Button size="small" onClick={f.navLast} disabled={f.isEditing || !f.records.length}>&gt;|</Button>
        </div>
        <div className="space-y-4 mb-6">
          <div className="flex items-center gap-4">
            <label className="w-40 text-sm text-right">提繳工資 (F938_01)</label>
            <NumInput value={f.wage} onChange={f.setWage} disabled={f.mode !== 'add'} />
          </div>
          <div className="flex items-center gap-4">
            <label className="w-40 text-sm text-right">月提繳比率% (F938_12)</label>
            <NumInput value={f.rate} onChange={f.setRate} disabled={!f.isEditing} />
            <span className="text-xs text-gray-400">最大 6%</span>
          </div>
          <div className="flex items-center gap-4">
            <label className="w-40 text-sm text-right">月提繳金額 (F938_21)</label>
            <span className="text-sm">{f.isEditing ? f.monthlyPreview.toFixed(0) : (f.current?.f938_21 ?? 0)}</span>
          </div>
          <div className="flex items-center gap-4">
            <label className="w-40 text-sm text-right">日提繳金額 (F938_11)</label>
            <span className="text-sm">{f.isEditing ? f.dailyPreview.toFixed(1) : (f.current?.f938_11 ?? 0)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!f.isEditing ? (
            <>
              <Button type="primary" onClick={f.startAdd}>新增</Button>
              <Button onClick={f.startEdit} disabled={!f.current}>修改</Button>
              <Button danger onClick={handleDelete} disabled={!f.current}>刪除</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={f.save}>儲存</Button>
              <Button onClick={f.cancel}>取消</Button>
            </>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default P9138Form;
